import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card } from '@progress/kendo-react-layout';
import styles from './style.module.scss';
import { InfoField } from '@components-clean/core/info-field';
import Tooltip from '@components-clean/core/tooltip';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';

const RiskInformation = () => {
    const { current } = useContext(RiskLogContext);
    const [expanded, setExpanded] = React.useState(false);
    const risk = current.selectionState.selectedItem;
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const { t: tShared } = useTranslation('ram', { keyPrefix: 'shared' });
    const closedManually = useRef(false);
    const preventAutomaticOpen = useRef(false);

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            const body = entries[0];
            preventAutomaticOpen.current = false;

            if (body.contentRect.width <= 1024) {
                preventAutomaticOpen.current = true;
                setExpanded(false);
            }
        });
        observer.observe(document.body);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const openState = current.selectionState.selectedItemsCount === 1;
        if (!closedManually.current && !preventAutomaticOpen.current) setExpanded(openState);
    }, [current.selectionState]);

    return (
        <Card
            style={{
                width: expanded ? '235px' : risk?.score?.score === 1000 ? '39px' : '37px',
                marginRight: '10px',
                flexShrink: 0,
            }}
            data-testid="risk-log-score-area"
        >
            <>
                {current.selectionState.selectedItemsCount > 1 ? (
                    expanded ? (
                        <div className="k-h-full k-p-2.5">
                            <InfoField
                                type="Info"
                                message={t('grids.observations.info.multipleParentRecords')}
                                layout="center"
                            />
                        </div>
                    ) : (
                        <div className={styles.bodyCollapsed}>
                            <Tooltip position="right" anchorElement="target" parentTitle>
                                <span
                                    title={t('grids.observations.info.multipleParentRecords')}
                                    style={{ display: 'flex' }}
                                >
                                    <FontAwesomeIcon
                                        data-testid="info-field-icon"
                                        fontSize="20px"
                                        icon={['fal', 'circle-exclamation']}
                                    />{' '}
                                </span>
                            </Tooltip>
                        </div>
                    )
                ) : (
                    <div className={styles.body} style={{ padding: expanded ? '10px' : '0px' }}>
                        {expanded && (
                            <div style={{ flexGrow: 1 }}>
                                <div style={{ height: '100%', overflowY: 'auto' }}>
                                    <span className={styles.statement}>{risk?.statement}</span>
                                </div>
                            </div>
                        )}
                        <div className={styles.bodyCollapsed}>
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    closedManually.current = expanded;
                                    risk && current.selectionState.selectedItemsCount === 1 && setExpanded(!expanded);
                                }}
                                onKeyDown={() => {}}
                                style={{ cursor: 'pointer' }}
                            >
                                <FontAwesomeIcon icon={expanded ? ['fal', 'chevron-left'] : ['fal', 'chevron-right']} />
                                <div style={{ writingMode: 'sideways-lr', marginTop: '10px' }}>
                                    {tShared('statement')}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        </Card>
    );
};

export default RiskInformation;
